import { useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { TaskLocation } from '@/api';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { FETCH_TASK_LOCATIONS_QUERY_KEY } from './tasks-query-keys';

const PAGE_SIZE = 500;

/**
 * Every location a task has reported, newest first.
 *
 * The endpoint is paginated by timestamp, but a trail is only a trail whole:
 * every caller wants the lot, so this pages all the way through rather than
 * leaving the caller to — the same shape as `useFetchAllTasks`.
 *
 * The pages are held as the API returns them, because
 * `TaskLocationsProvider` splices a pushed location into the right page on a
 * socket update.
 */
export function useFetchTaskLocations(taskId: string): {
    status: 'error' | 'success' | 'pending';
    locations: TaskLocation[];
    refetch: () => void;
} {
    // The client is swapped out on token refresh, so never capture it: read
    // `openApiRef.current` at call time or you keep using a dead token.
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const {
        data,
        status,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
        refetch
    } = useInfiniteQuery({
        queryKey: [FETCH_TASK_LOCATIONS_QUERY_KEY, taskId],
        queryFn: ({ pageParam }) => {
            const limit = PAGE_SIZE;
            const fromTs = pageParam || undefined;
            return openApiRef.current.tasks
                .getLocations(taskId, { limit, fromTs })
                .then((res) => {
                    return res.data.data.locations;
                });
        },
        initialPageParam: '',
        getNextPageParam: (lastPage) => {
            if (!lastPage.nextTs) return undefined;
            return lastPage.nextTs as unknown as string;
        },
        // Nothing to read before a task has been chosen.
        enabled: taskId !== ''
    });

    useEffect(() => {
        if (!isFetchingNextPage && hasNextPage) {
            void fetchNextPage();
        }
    }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

    const locations = useMemo(() => {
        return data?.pages.flatMap((page) => page.data) ?? [];
    }, [data]);

    return {
        status,
        locations,
        refetch: () => {
            void refetch();
        }
    };
}
